import { useCallback, useState } from 'react';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { FixedSaveBar } from 'foundations-components/transitional/components/FixedSaveBar';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useGetChanges, useIsSaveable, useOnResetAll, useTotalChanges } from 'framework-listing-lib/internal/FrameworkDataTable/hooks/useInlineEdit';
import useSaveCrmObjects from 'framework-listing-lib/internal/FrameworkDataTable/hooks/useSaveCrmObjects';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import { useLowercaseCrmObjectTypeName } from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useUpdateHighlySensitivePropertyValues } from 'customer-data-properties/dataSensitivity/useUpdateHighlySensitivePropertyValues';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function InlineEditSaveBar() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    lowerCaseObjectTypeNameSingular,
    lowerCaseObjectTypeNamePlural,
    capitalizedObjectTypeNameSingular,
    capitalizedObjectTypeNamePlural
  } = useLowercaseCrmObjectTypeName();
  const totalChanges = useTotalChanges();
  const isSaveable = useIsSaveable();
  const objectTypeId = useObjectTypeId();
  const onResetAll = useOnResetAll();
  const getChanges = useGetChanges();
  const saveCrmObjects = useSaveCrmObjects();
  const updateHighlySensitivePropertyValues = useUpdateHighlySensitivePropertyValues();
  const handleCancelClick = useCallback(() => {
    const changes = getChanges();
    const numberOfProperties = [...new Set(Object.keys(changes).map(objectId => Object.keys(changes[objectId])).flat())].length;
    trackCrmTableInteraction({
      action: 'cancel-inline-editing',
      numberOfProperties,
      totalRecords: Object.keys(changes).length
    });
    onResetAll();
  }, [getChanges, onResetAll, trackCrmTableInteraction]);
  const handleSaveClick = useCallback(() => {
    const changes = getChanges();
    const numberOfProperties = [...new Set(Object.keys(changes).map(objectId => Object.keys(changes[objectId])).flat())].length;
    setIsLoading(true);
    saveCrmObjects(changes).then(() => {
      setIsLoading(false);
      trackCrmTableInteraction({
        action: 'save-inline-editing-success',
        numberOfProperties,
        totalRecords: Object.keys(changes).length
      });

      // Update internal cache for each affected row
      Object.keys(changes).forEach(objectId => {
        updateHighlySensitivePropertyValues({
          objectTypeId,
          objectId,
          values: changes[objectId]
        });
        listingLibObserver.updatePropertyValues({
          selectedIds: [parseInt(objectId, 10)],
          propertyValues: Object.entries(changes[objectId]).map(([name, value]) => ({
            name,
            value
          }))
        });
      });
      onResetAll();
      alertSuccess({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-test-id": "inline-edit-success-message",
          message: "frameworkListingLib.inlineEdit.saveBar.success.message",
          options: {
            count: Object.keys(changes).length,
            objectTypeNamePlural: lowerCaseObjectTypeNamePlural,
            objectTypeNameSingular: lowerCaseObjectTypeNameSingular,
            capitalizedObjectTypeNamePlural,
            capitalizedObjectTypeNameSingular
          }
        }),
        options: {
          'data-test-id': 'inline-edit-success-alert'
        }
      });
    }).catch(() => {
      setIsLoading(false);
      trackCrmTableInteraction({
        action: 'save-inline-editing-failure',
        numberOfProperties,
        totalRecords: Object.keys(changes).length
      });
      alertFailure({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.alerts.failure.message"
        }),
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "frameworkListingLib.inlineEdit.saveBar.failure.title_jsx",
          options: {
            count: Object.keys(changes).length,
            objectTypeNamePlural: lowerCaseObjectTypeNamePlural,
            objectTypeNameSingular: lowerCaseObjectTypeNameSingular,
            capitalizedObjectTypeNamePlural,
            capitalizedObjectTypeNameSingular
          }
        })
      });
    });
  }, [capitalizedObjectTypeNamePlural, capitalizedObjectTypeNameSingular, getChanges, lowerCaseObjectTypeNamePlural, lowerCaseObjectTypeNameSingular, objectTypeId, onResetAll, saveCrmObjects, trackCrmTableInteraction, updateHighlySensitivePropertyValues]);
  if (totalChanges === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(FixedSaveBar, {
    "data-test-id": "inline-editing-savebar",
    children: [/*#__PURE__*/_jsx(Tooltip, {
      disabled: isSaveable,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.inlineEdit.saveBar.invalidEditsTooltip"
      }),
      children: /*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "save-inline-editing",
        use: "primary",
        disabled: !isSaveable,
        loading: isLoading,
        onClick: handleSaveClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.inlineEdit.saveBar.save"
        })
      })
    }), /*#__PURE__*/_jsx(Button, {
      use: "secondary",
      onClick: handleCancelClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.inlineEdit.saveBar.cancel"
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingInlineStart: "300",
      as: "span",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.inlineEdit.saveBar.editCount",
        options: {
          count: totalChanges
        }
      })
    })]
  });
}